// Fantasy Tavern skin: a pixel knight walks the road to the tavern.
//   idle    - knight rests at the tavern by a campfire
//   pending - knight walks; progress eases him toward the door
//   failure - knight freezes where he is and a monster blocks the road
//   success - knight at the glowing door, whole path floods green
import { makeScene, mix, type Palette, type SceneCtx } from '../scene/engine';
import type { SkinDef } from './types';

const colors = (g: (n: string) => string): Palette => ({
  skyTop: mix(g('--bg-raise'), g('--accent'), 0.12), skyBot: g('--bg-raise'),
  ground: g('--surface'), groundLo: g('--bg'), edge: g('--line-bright'), dash: g('--line'),
  accent: g('--accent'), green: g('--green'), red: g('--red'), amber: g('--amber'), gild: g('--gild'),
  woodHi: '#6b4a24', woodMid: '#4a3218', woodLo: '#2f1f0e', flameHi: '#fff2c2', flameLo: g('--amber'),
  steel: '#9aa7b3', steelHi: '#cdd6de', steelLo: '#5b6773', blade: '#dfe6ec', leather: '#4a3218',
  mBody: '#8f4640', mBodyHi: '#a5564e', mHorn: '#6f322c', mEye: '#ffe9b0', mPupil: '#3a1410',
  drgn: '#6e2b28', drgnWing: '#8f4640',
});

function torch(s: SceneCtx, C: Palette, x: number, f: number) {
  const { px, groundY: GY } = s;
  px(x, GY - 14, 2, 14, C.woodMid);
  const h = 5 + (f % 2 ? 1 : 0), w = 4 + (f % 3 ? 0 : 1);
  px(x - 1, GY - 14 - h, w, h, C.flameLo); px(x, GY - 14 - h + 1, 2, h - 2, C.flameHi);
}
function campfire(s: SceneCtx, C: Palette, x: number, f: number) {
  const { px, groundY: GY } = s; const by = GY + 1;
  px(x - 4, by - 1, 9, 2, C.woodHi); px(x - 1, by - 6 - f, 3, 5, C.flameLo); px(x, by - 5 - f, 1, 3, C.flameHi);
}
function tavern(s: SceneCtx, C: Palette, green: boolean) {
  const { px, glow, groundY: GY, W } = s; const bx = W - 40, by = GY;
  if (green) glow(bx - 6, by - 30, 40, 34, C.green, 0.16);
  for (let i = 0; i < 10; i++) px(bx + 2 + i, by - 26 - i, 32 - 2 * i, 2, i < 1 ? C.gild : C.woodMid);
  px(bx + 2, by - 26, 32, 2, C.gild);
  px(bx, by - 18, 36, 18, C.woodLo); px(bx, by - 18, 36, 1, C.edge); px(bx, by - 1, 36, 1, C.edge); px(bx, by - 18, 1, 18, C.edge); px(bx + 35, by - 18, 1, 18, C.edge);
  px(bx + 5, by - 13, 6, 6, C.amber); px(bx + 25, by - 13, 6, 6, C.amber);
  px(bx + 15, by - 12, 7, 12, green ? mix(C.green, C.woodLo, 0.35) : '#241708');
  if (green) { px(bx + 15, by - 12, 7, 1, C.green); px(bx + 15, by - 12, 1, 12, C.green); px(bx + 21, by - 12, 1, 12, C.green); }
  px(bx + 20, by - 6, 1, 1, C.gild);
  px(bx + 30, by - 24, 2, 4, C.woodHi); px(bx + 27, by - 21, 9, 6, C.woodHi); px(bx + 28, by - 20, 7, 4, C.gild);
}
function knight(s: SceneCtx, C: Palette, x: number, walkF: number, tint: string, resting: boolean) {
  const { px, groundY: GY } = s;
  const fy = GY + 2, bob = !resting && walkF % 2 ? -1 : 0, y = fy - 16 + bob;
  if (resting) { px(x + 3, fy - 4, 2, 4, C.steelLo); px(x + 7, fy - 4, 2, 4, C.steelLo); }
  else { const st = walkF % 2 ? 1 : -1; px(x + 3, fy - 4, 2, 4, C.steelLo); px(x + 7 + st, fy - 4, 2, 4, C.steelLo); }
  px(x + 1, y + 8, 3, 6, tint); px(x + 1, y + 8, 1, 6, C.gild); px(x + 2, y + 10, 1, 2, C.gild);      // shield
  px(x + 3, y + 7, 7, 7, C.steel); px(x + 3, y + 7, 7, 1, C.steelHi); px(x + 4, y + 9, 5, 2, tint);   // torso + tabard
  px(x + 3, y, 7, 7, C.steel); px(x + 3, y, 7, 1, C.steelHi); px(x + 4, y + 3, 5, 1, C.woodLo); px(x + 5, y - 2, 3, 2, tint); // helm + plume
  if (resting) { px(x + 6, y + 2, 2, 15, C.blade); px(x + 4, y + 3, 6, 1, C.gild); px(x + 6, y + 1, 2, 2, C.leather); px(x + 9, y + 9, 2, 2, C.steelLo); }
  else { px(x + 9, y + 8, 2, 3, C.steelLo); px(x + 11, y - 3, 2, 12, C.blade); px(x + 10, y + 6, 4, 1, C.gild); px(x + 11, y + 7, 2, 2, C.leather); }
}
function monster(s: SceneCtx, C: Palette, x: number) {
  const { px, groundY: GY } = s; const by = GY + 2, y = by - 14;
  px(x, y, 2, 3, C.mHorn); px(x + 12, y, 2, 3, C.mHorn);
  px(x + 1, y + 2, 12, 12, C.mBody); px(x + 2, y + 5, 10, 8, C.mBodyHi); px(x, y + 5, 1, 6, C.mBody); px(x + 13, y + 5, 1, 6, C.mBody);
  px(x + 3, y + 5, 3, 3, C.mEye); px(x + 8, y + 5, 3, 3, C.mEye); px(x + 4, y + 6, 1, 1, C.mPupil); px(x + 9, y + 6, 1, 1, C.mPupil);
  for (let i = 0; i < 5; i++) px(x + 2 + i * 2, y + 10 + (i % 2), 1, 2, C.mEye);
}

// A little dragon gliding across the sky, facing right, wings flapping with `flap`.
// Silhouette-scale to read as distant background life; static (one pose) under
// reduced motion since `flap` is then a fixed frame.
function dragon(s: SceneCtx, C: Palette, x: number, y: number, flap: number) {
  const { px } = s;
  const body = C.drgn, wing = C.drgnWing;
  px(x, y + 1, 3, 1, body);                                                   // tail trailing left
  px(x + 3, y, 5, 2, body);                                                   // body
  px(x + 8, y - 1, 2, 2, body); px(x + 10, y - 1, 1, 1, body);                // neck + head + snout
  px(x + 9, y - 1, 1, 1, C.mEye);                                             // eye glint
  if (flap) { px(x + 4, y - 2, 1, 2, wing); px(x + 6, y - 2, 1, 2, wing); px(x + 5, y - 1, 1, 1, wing); }  // wings up
  else { px(x + 4, y + 2, 1, 2, wing); px(x + 6, y + 2, 1, 2, wing); px(x + 5, y + 2, 1, 1, wing); }        // wings down
}

const draw = (C: Palette, s: SceneCtx) => {
  const { px, mix: m, W, H, groundY: GY, pathL: PL, state: st, heroX: hx, walkF, flameF } = s;
  const tint = st === 'failure' ? C.red : st === 'success' ? C.green : C.accent;
  const allDone = st === 'success';
  // sky + distant hills
  for (let y = 0; y < GY; y++) px(0, y, W, 1, m(C.skyTop, C.skyBot, y / GY));
  px(24, GY - 6, 48, 6, C.skyBot); px(84, GY - 10, 66, 10, m(C.skyBot, C.groundLo, 0.4)); px(150, GY - 6, 54, 6, C.skyBot);
  // dragons drifting across the twilight sky — ambient background life, behind the props.
  // walkF drives the glide (0 under reduced motion → they rest on-screen); flameF flaps the wings.
  const dspan = W + 28;
  dragon(s, C, ((walkF + 30) % dspan) - 14, 8, flameF);
  dragon(s, C, ((walkF + 150) % dspan) - 14, 16, (flameF + 1) % 2);
  // ground
  for (let y = GY; y < H; y++) px(0, y, W, 1, m(C.ground, C.groundLo, (y - GY) / (H - GY)));
  px(0, GY - 1, W, 2, C.edge);
  // path (trodden trail behind the knight; whole path green on success)
  const progX = st === 'idle' ? PL : hx + 6;
  for (let x = PL; x < W - 8; x += 10) { const done = allDone || x < progX; px(x, GY + 3, 6, 2, done ? tint : C.dash); }
  px(PL, GY + 2, allDone ? W - 8 - PL : Math.max(0, progX - PL), 1, m(tint, C.ground, 0.3));
  // props + cast
  torch(s, C, 30, walkF); torch(s, C, 104, walkF + 1); torch(s, C, 166, walkF + 2);
  tavern(s, C, st === 'success');
  if (st === 'idle') campfire(s, C, W - 64, flameF);
  knight(s, C, hx, walkF, tint, st === 'idle');
  if (st === 'failure') monster(s, C, hx + 14);
};

export const fantasy: SkinDef = {
  label: 'Fantasy Tavern',
  PromoteScene: makeScene({ colors, draw }),
  lines: {
    pending: 'Onward — the keep awaits!',
    failure: 'A beast bars the road!',
    success: 'The keep is ours! 🍺',
  },
};
